import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Image from '../../../components/AppImage';
import InteractiveImageHotspot from '../../../components/ui/InteractiveImageHotspot';

const BikeImageAnalyzer = ({ 
  bikeImage, 
  analysisData, 
  onComponentSelect = () => {},
  className = "" 
}) => {
  const [selectedComponent, setSelectedComponent] = useState(null);
  const [imageLoaded, setImageLoaded] = useState(false);

  const hotspots = [
    {
      id: 1,
      x: 25,
      y: 45,
      component: "Front Wheel",
      category: "Wheels & Tires",
      status: "upgrade",
      description: "Basic aluminum rim with entry-level hub. Upgrade potential for better aerodynamics and weight reduction.",
      upgrade: {
        name: "Carbon Fiber Wheelset",
        price: 1299,
        benefits: ["30% lighter", "Better aerodynamics", "Improved stiffness"]
      }
    },
    {
      id: 2,
      x: 75,
      y: 45,
      component: "Rear Wheel",
      category: "Wheels & Tires",
      status: "upgrade",
      description: "Matches front wheel. Consider upgrading as a complete wheelset for optimal performance.",
      upgrade: {
        name: "Carbon Fiber Wheelset",
        price: 1299,
        benefits: ["30% lighter", "Better aerodynamics", "Improved stiffness"]
      }
    },
    {
      id: 3,
      x: 50,
      y: 35,
      component: "Frame",
      category: "Frame & Fork",
      status: "good",
      description: "Aluminum frame in excellent condition. No immediate upgrades needed.",
      upgrade: null
    },
    {
      id: 4,
      x: 60,
      y: 55,
      component: "Drivetrain",
      category: "Drivetrain",
      status: "upgrade",
      description: "Shimano 105 groupset showing wear. Consider upgrading to Ultegra for better performance.",
      upgrade: {
        name: "Shimano Ultegra R8000",
        price: 899,
        benefits: ["Smoother shifting", "Better durability", "Weight reduction"]
      }
    },
    {
      id: 5,
      x: 35,
      y: 25,
      component: "Handlebars",
      category: "Cockpit",
      status: "good",
      description: "Carbon handlebars in good condition with proper tape.",
      upgrade: null
    },
    {
      id: 6,
      x: 45,
      y: 65,
      component: "Saddle",
      category: "Comfort",
      status: "upgrade",
      description: "Basic saddle may benefit from ergonomic upgrade for longer rides.",
      upgrade: {
        name: "Fizik Antares R3",
        price: 189,
        benefits: ["Better comfort", "Pressure relief", "Lightweight"]
      }
    }
  ];

  const handleHotspotClick = (hotspot, action = 'select') => {
    if (action === 'select') {
      setSelectedComponent(hotspot);
      onComponentSelect(hotspot);
    } else if (action === 'view-details') {
      onComponentSelect(hotspot, 'view-details');
    } else if (action === 'add-to-wishlist') {
      onComponentSelect(hotspot, 'add-to-wishlist');
    }
  };

  const getAnalysisSummary = () => {
    const upgradeableComponents = hotspots?.filter(h => h?.status === 'upgrade');
    const goodComponents = hotspots?.filter(h => h?.status === 'good');
    
    return {
      total: hotspots?.length,
      upgradeable: upgradeableComponents?.length,
      good: goodComponents?.length,
      totalUpgradeCost: upgradeableComponents?.reduce((sum, h) => sum + (h?.upgrade?.price || 0), 0)
    };
  };

  const summary = getAnalysisSummary();

  return (
    <div className={`bg-card border border-border rounded-lg overflow-hidden ${className}`}>
      {/* Header */}
      <div className="p-4 border-b border-border">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-lg font-semibold text-foreground">Bike Analysis</h2>
            <p className="text-sm text-muted-foreground">
              Click on highlighted components for detailed analysis
            </p>
          </div>
          <div className="flex items-center space-x-4 text-sm">
            <div className="flex items-center space-x-1">
              <div className="w-3 h-3 bg-success rounded-full"></div>
              <span className="text-muted-foreground">{summary?.good} Good</span>
            </div>
            <div className="flex items-center space-x-1">
              <div className="w-3 h-3 bg-warning rounded-full"></div>
              <span className="text-muted-foreground">{summary?.upgradeable} Upgradeable</span>
            </div>
          </div>
        </div>
      </div>
      {/* Interactive Image */}
      <div className="p-4">
        <InteractiveImageHotspot
          imageSrc={bikeImage}
          imageAlt="Analyzed bike with component hotspots"
          hotspots={hotspots}
          onHotspotClick={handleHotspotClick}
          className="w-full"
        />
      </div>
      {/* Analysis Summary */}
      <div className="p-4 border-t border-border bg-muted/30">
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          <div className="text-center">
            <div className="text-2xl font-bold text-foreground">{summary?.total}</div>
            <div className="text-xs text-muted-foreground">Components</div>
          </div>
          <div className="text-center">
            <div className="text-2xl font-bold text-warning">{summary?.upgradeable}</div>
            <div className="text-xs text-muted-foreground">Upgradeable</div>
          </div>
          <div className="text-center">
            <div className="text-2xl font-bold text-success">{summary?.good}</div>
            <div className="text-xs text-muted-foreground">Good Condition</div>
          </div>
          <div className="text-center">
            <div className="text-2xl font-bold text-accent">${summary?.totalUpgradeCost?.toLocaleString()}</div>
            <div className="text-xs text-muted-foreground">Total Upgrades</div>
          </div>
        </div>
      </div>
      {/* Quick Actions */}
      <div className="p-4 border-t border-border">
        <div className="flex flex-wrap gap-2">
          <button
            onClick={() => onComponentSelect(null, 'share')}
            className="flex items-center space-x-2 px-3 py-2 bg-muted text-muted-foreground rounded-md hover:bg-muted/80 transition-colors interactive-scale text-sm"
          >
            <Icon name="Share2" size={16} />
            <span>Share Analysis</span>
          </button>
          <button
            onClick={() => onComponentSelect(null, 'download')}
            className="flex items-center space-x-2 px-3 py-2 bg-muted text-muted-foreground rounded-md hover:bg-muted/80 transition-colors interactive-scale text-sm"
          >
            <Icon name="Download" size={16} />
            <span>Download Report</span>
          </button>
          <button
            onClick={() => onComponentSelect(null, 'new-analysis')}
            className="flex items-center space-x-2 px-3 py-2 bg-accent text-accent-foreground rounded-md hover:bg-accent/90 transition-colors interactive-scale text-sm"
          >
            <Icon name="Camera" size={16} />
            <span>New Analysis</span>
          </button>
        </div>
      </div>
    </div>
  );
};

export default BikeImageAnalyzer;